import { useState, useEffect, Fragment } from 'react';
import { X, Mail, Lock, User, Eye, EyeOff, ArrowLeft, Loader2 } from 'lucide-react';
import { api, dashboardUrl } from '../lib/api';

interface AuthModalProps {
  isOpen: boolean;
  onClose: () => void;
  initialMode?: 'login' | 'register';
}

type AuthView = 'login' | 'register' | 'forgot-password' | 'verify-email' | 'check-email';

interface FormState {
  email: string;
  password: string;
  name: string;
  confirmPassword: string;
}

export default function AuthModal({ isOpen, onClose, initialMode = 'login' }: AuthModalProps) {
  const [view, setView] = useState<AuthView>(initialMode);
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [form, setForm] = useState<FormState>({
    email: '',
    password: '',
    name: '',
    confirmPassword: '',
  });

  useEffect(() => {
    if (isOpen) {
      setView(initialMode);
      setError('');
      setSuccessMessage('');
    }
  }, [isOpen, initialMode]);

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  const resetForm = () => {
    setForm({ email: '', password: '', name: '', confirmPassword: '' });
    setError('');
    setSuccessMessage('');
    setShowPassword(false);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setForm(prev => ({ ...prev, [e.target.name]: e.target.value }));
    setError('');
  };

  const handleGoogleLogin = () => {
    // Redirect to API's Google OAuth endpoint with dashboard as redirect
    // Default to api.watchthesize.com if VITE_API_URL is not set
    const apiUrl = import.meta.env.VITE_API_URL || 'https://api.watchthesize.com';
    window.location.href = `${apiUrl}/api/auth/google?redirect=${encodeURIComponent(dashboardUrl)}`;
  };

  const handleEmailLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');

    try {
      const response = await api.post<{
        success: boolean;
        data?: { token: string; refresh_token: string; user: unknown };
        error?: string;
        code?: string;
      }>('/api/auth/login', {
        email: form.email,
        password: form.password,
      });

      if (response.success && response.data) {
        localStorage.setItem('token', response.data.token);
        localStorage.setItem('refreshToken', response.data.refresh_token);
        // Redirect to dashboard
        window.location.href = dashboardUrl;
      } else {
        if (response.code === 'EMAIL_NOT_VERIFIED') {
          setView('verify-email');
        } else {
          setError(response.error || 'Login failed');
        }
      }
    } catch (err: unknown) {
      const error = err as Error;
      setError(error.message || 'Login failed. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleRegister = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');

    if (form.password !== form.confirmPassword) {
      setError('Passwords do not match');
      setIsLoading(false);
      return;
    }

    try {
      const response = await api.post<{
        success: boolean;
        message?: string;
        error?: string;
      }>('/api/auth/register', {
        email: form.email,
        password: form.password,
        name: form.name || undefined,
      });

      if (response.success) {
        setView('check-email');
        setSuccessMessage(response.message || 'Registration successful! Check your email.');
      } else {
        setError(response.error || 'Registration failed');
      }
    } catch (err: unknown) {
      const error = err as Error;
      setError(error.message || 'Registration failed. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleForgotPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');

    try {
      const response = await api.post<{
        success: boolean;
        message?: string;
        error?: string;
      }>('/api/auth/forgot-password', {
        email: form.email,
      });

      if (response.success) {
        setSuccessMessage(response.message || 'Check your email for reset instructions.');
        setView('check-email');
      } else {
        setError(response.error || 'Failed to send reset email');
      }
    } catch (err: unknown) {
      const error = err as Error;
      setError(error.message || 'Failed to send reset email.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleResendVerification = async () => {
    setIsLoading(true);
    setError('');

    try {
      const response = await api.post<{
        success: boolean;
        message?: string;
        error?: string;
      }>('/api/auth/resend-verification', {
        email: form.email,
      });

      if (response.success) {
        setSuccessMessage(response.message || 'Verification email sent!');
      } else {
        setError(response.error || 'Failed to send verification email');
      }
    } catch (err: unknown) {
      const error = err as Error;
      setError(error.message || 'Failed to send verification email.');
    } finally {
      setIsLoading(false);
    }
  };

  if (!isOpen) return null;

  const renderLoginForm = () => (
    <form onSubmit={handleEmailLogin} className="space-y-4">
      <div>
        <label className="block text-sm font-medium text-zinc-300 mb-1.5">Email</label>
        <div className="relative">
          <Mail className="absolute left-3.5 top-1/2 -translate-y-1/2 w-5 h-5 text-zinc-500" />
          <input
            type="email"
            name="email"
            value={form.email}
            onChange={handleInputChange}
            required
            placeholder="you@example.com"
            className="w-full pl-11 pr-4 py-3 rounded-xl bg-white/5 border border-white/10 text-white placeholder-zinc-500 focus:outline-none focus:ring-2 focus:ring-violet-500/50 focus:border-violet-500/50"
          />
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-zinc-300 mb-1.5">Password</label>
        <div className="relative">
          <Lock className="absolute left-3.5 top-1/2 -translate-y-1/2 w-5 h-5 text-zinc-500" />
          <input
            type={showPassword ? 'text' : 'password'}
            name="password"
            value={form.password}
            onChange={handleInputChange}
            required
            placeholder="••••••••"
            className="w-full pl-11 pr-11 py-3 rounded-xl bg-white/5 border border-white/10 text-white placeholder-zinc-500 focus:outline-none focus:ring-2 focus:ring-violet-500/50 focus:border-violet-500/50"
          />
          <button
            type="button"
            onClick={() => setShowPassword(!showPassword)}
            className="absolute right-3.5 top-1/2 -translate-y-1/2 text-zinc-500 hover:text-zinc-300"
          >
            {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
          </button>
        </div>
      </div>

      <div className="flex justify-end">
        <button
          type="button"
          onClick={() => { resetForm(); setView('forgot-password'); }}
          className="text-sm text-violet-400 hover:text-violet-300 transition-colors"
        >
          Forgot password?
        </button>
      </div>

      <button
        type="submit"
        disabled={isLoading}
        className="w-full py-3 rounded-xl bg-gradient-to-r from-violet-500 to-fuchsia-500 text-white font-semibold shadow-lg shadow-violet-500/25 hover:shadow-violet-500/40 transition-all hover:scale-[1.01] disabled:opacity-50 disabled:hover:scale-100 flex items-center justify-center gap-2"
      >
        {isLoading ? <Loader2 className="w-5 h-5 animate-spin" /> : 'Sign In'}
      </button>

      <div className="relative my-6">
        <div className="absolute inset-0 flex items-center">
          <div className="w-full border-t border-white/10" />
        </div>
        <div className="relative flex justify-center text-sm">
          <span className="px-3 bg-zinc-900 text-zinc-500">or continue with</span>
        </div>
      </div>

      <button
        type="button"
        onClick={handleGoogleLogin}
        className="w-full py-3 rounded-xl bg-white/5 border border-white/10 text-white font-medium hover:bg-white/10 transition-all flex items-center justify-center gap-3"
      >
        <svg className="w-5 h-5" viewBox="0 0 24 24">
          <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
          <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
          <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
          <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
        </svg>
        Continue with Google
      </button>

      <p className="text-center text-sm text-zinc-500 mt-6">
        Don't have an account?{' '}
        <button
          type="button"
          onClick={() => { resetForm(); setView('register'); }}
          className="text-violet-400 hover:text-violet-300 font-medium"
        >
          Sign up
        </button>
      </p>
    </form>
  );

  const renderRegisterForm = () => (
    <form onSubmit={handleRegister} className="space-y-4">
      <div>
        <label className="block text-sm font-medium text-zinc-300 mb-1.5">Name (optional)</label>
        <div className="relative">
          <User className="absolute left-3.5 top-1/2 -translate-y-1/2 w-5 h-5 text-zinc-500" />
          <input
            type="text"
            name="name"
            value={form.name}
            onChange={handleInputChange}
            placeholder="John Doe"
            className="w-full pl-11 pr-4 py-3 rounded-xl bg-white/5 border border-white/10 text-white placeholder-zinc-500 focus:outline-none focus:ring-2 focus:ring-violet-500/50 focus:border-violet-500/50"
          />
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-zinc-300 mb-1.5">Email</label>
        <div className="relative">
          <Mail className="absolute left-3.5 top-1/2 -translate-y-1/2 w-5 h-5 text-zinc-500" />
          <input
            type="email"
            name="email"
            value={form.email}
            onChange={handleInputChange}
            required
            placeholder="you@example.com"
            className="w-full pl-11 pr-4 py-3 rounded-xl bg-white/5 border border-white/10 text-white placeholder-zinc-500 focus:outline-none focus:ring-2 focus:ring-violet-500/50 focus:border-violet-500/50"
          />
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-zinc-300 mb-1.5">Password</label>
        <div className="relative">
          <Lock className="absolute left-3.5 top-1/2 -translate-y-1/2 w-5 h-5 text-zinc-500" />
          <input
            type={showPassword ? 'text' : 'password'}
            name="password"
            value={form.password}
            onChange={handleInputChange}
            required
            placeholder="••••••••"
            minLength={8}
            className="w-full pl-11 pr-11 py-3 rounded-xl bg-white/5 border border-white/10 text-white placeholder-zinc-500 focus:outline-none focus:ring-2 focus:ring-violet-500/50 focus:border-violet-500/50"
          />
          <button
            type="button"
            onClick={() => setShowPassword(!showPassword)}
            className="absolute right-3.5 top-1/2 -translate-y-1/2 text-zinc-500 hover:text-zinc-300"
          >
            {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
          </button>
        </div>
        <p className="text-xs text-zinc-500 mt-1.5">
          Min 8 characters, 1 uppercase, 1 lowercase, 1 number
        </p>
      </div>

      <div>
        <label className="block text-sm font-medium text-zinc-300 mb-1.5">Confirm Password</label>
        <div className="relative">
          <Lock className="absolute left-3.5 top-1/2 -translate-y-1/2 w-5 h-5 text-zinc-500" />
          <input
            type="password"
            name="confirmPassword"
            value={form.confirmPassword}
            onChange={handleInputChange}
            required
            placeholder="••••••••"
            className="w-full pl-11 pr-4 py-3 rounded-xl bg-white/5 border border-white/10 text-white placeholder-zinc-500 focus:outline-none focus:ring-2 focus:ring-violet-500/50 focus:border-violet-500/50"
          />
        </div>
      </div>

      <button
        type="submit"
        disabled={isLoading}
        className="w-full py-3 rounded-xl bg-gradient-to-r from-violet-500 to-fuchsia-500 text-white font-semibold shadow-lg shadow-violet-500/25 hover:shadow-violet-500/40 transition-all hover:scale-[1.01] disabled:opacity-50 disabled:hover:scale-100 flex items-center justify-center gap-2"
      >
        {isLoading ? <Loader2 className="w-5 h-5 animate-spin" /> : 'Create Account'}
      </button>

      <div className="relative my-6">
        <div className="absolute inset-0 flex items-center">
          <div className="w-full border-t border-white/10" />
        </div>
        <div className="relative flex justify-center text-sm">
          <span className="px-3 bg-zinc-900 text-zinc-500">or continue with</span>
        </div>
      </div>

      <button
        type="button"
        onClick={handleGoogleLogin}
        className="w-full py-3 rounded-xl bg-white/5 border border-white/10 text-white font-medium hover:bg-white/10 transition-all flex items-center justify-center gap-3"
      >
        <svg className="w-5 h-5" viewBox="0 0 24 24">
          <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
          <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
          <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
          <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
        </svg>
        Continue with Google
      </button>

      <p className="text-center text-sm text-zinc-500 mt-6">
        Already have an account?{' '}
        <button
          type="button"
          onClick={() => { resetForm(); setView('login'); }}
          className="text-violet-400 hover:text-violet-300 font-medium"
        >
          Sign in
        </button>
      </p>
    </form>
  );

  const renderForgotPasswordForm = () => (
    <form onSubmit={handleForgotPassword} className="space-y-4">
      <button
        type="button"
        onClick={() => { resetForm(); setView('login'); }}
        className="flex items-center gap-2 text-sm text-zinc-400 hover:text-white transition-colors mb-4"
      >
        <ArrowLeft className="w-4 h-4" />
        Back to login
      </button>

      <div>
        <label className="block text-sm font-medium text-zinc-300 mb-1.5">Email</label>
        <div className="relative">
          <Mail className="absolute left-3.5 top-1/2 -translate-y-1/2 w-5 h-5 text-zinc-500" />
          <input
            type="email"
            name="email"
            value={form.email}
            onChange={handleInputChange}
            required
            placeholder="you@example.com"
            className="w-full pl-11 pr-4 py-3 rounded-xl bg-white/5 border border-white/10 text-white placeholder-zinc-500 focus:outline-none focus:ring-2 focus:ring-violet-500/50 focus:border-violet-500/50"
          />
        </div>
        <p className="text-xs text-zinc-500 mt-1.5">
          We'll send you a link to reset your password.
        </p>
      </div>

      <button
        type="submit"
        disabled={isLoading}
        className="w-full py-3 rounded-xl bg-gradient-to-r from-violet-500 to-fuchsia-500 text-white font-semibold shadow-lg shadow-violet-500/25 hover:shadow-violet-500/40 transition-all hover:scale-[1.01] disabled:opacity-50 disabled:hover:scale-100 flex items-center justify-center gap-2"
      >
        {isLoading ? <Loader2 className="w-5 h-5 animate-spin" /> : 'Send Reset Link'}
      </button>
    </form>
  );

  const renderVerifyEmail = () => (
    <div className="text-center py-4">
      <button
        type="button"
        onClick={() => { resetForm(); setView('login'); }}
        className="flex items-center gap-2 text-sm text-zinc-400 hover:text-white transition-colors mb-6"
      >
        <ArrowLeft className="w-4 h-4" />
        Back to login
      </button>

      <div className="w-16 h-16 rounded-full bg-amber-500/20 flex items-center justify-center mx-auto mb-4">
        <Mail className="w-8 h-8 text-amber-400" />
      </div>
      <h3 className="text-xl font-semibold text-white mb-2">Verify Your Email</h3>
      <p className="text-zinc-400 text-sm mb-6">
        Please verify your email address before signing in. Check your inbox for the verification link.
      </p>
      <button
        onClick={handleResendVerification}
        disabled={isLoading}
        className="text-violet-400 hover:text-violet-300 text-sm font-medium flex items-center justify-center gap-2 mx-auto"
      >
        {isLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
        Resend verification email
      </button>
    </div>
  );

  const renderCheckEmail = () => (
    <div className="text-center py-4">
      <div className="w-16 h-16 rounded-full bg-emerald-500/20 flex items-center justify-center mx-auto mb-4">
        <Mail className="w-8 h-8 text-emerald-400" />
      </div>
      <h3 className="text-xl font-semibold text-white mb-2">Check Your Email</h3>
      <p className="text-zinc-400 text-sm mb-6">
        {successMessage || 'We sent you an email with further instructions.'}
      </p>
      <button
        onClick={() => { resetForm(); setView('login'); }}
        className="text-violet-400 hover:text-violet-300 text-sm font-medium"
      >
        Back to login
      </button>
    </div>
  );

  const getTitle = () => {
    switch (view) {
      case 'login': return 'Welcome Back';
      case 'register': return 'Create Account';
      case 'forgot-password': return 'Reset Password';
      case 'verify-email': return 'Verification Required';
      case 'check-email': return 'Email Sent';
      default: return '';
    }
  };

  const getSubtitle = () => {
    switch (view) {
      case 'login': return 'Sign in to your WatchTheSize account';
      case 'register': return 'Start monitoring your servers today';
      case 'forgot-password': return 'Enter your email to reset password';
      default: return '';
    }
  };

  return (
    <Fragment>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
        <div
          className="relative w-full max-w-md bg-zinc-900 rounded-2xl border border-white/10 shadow-2xl overflow-hidden"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Close button */}
          <button
            onClick={onClose}
            className="absolute top-4 right-4 p-2 rounded-lg text-zinc-500 hover:text-white hover:bg-white/10 transition-colors z-10"
          >
            <X className="w-5 h-5" />
          </button>

          {/* Header */}
          <div className="pt-8 pb-4 px-8 text-center">
            <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-gradient-to-br from-violet-500 to-fuchsia-500 text-xl mx-auto mb-4 shadow-lg shadow-violet-500/25">
              📊
            </div>
            <h2 className="text-2xl font-bold text-white mb-1">{getTitle()}</h2>
            {getSubtitle() && (
              <p className="text-sm text-zinc-400">{getSubtitle()}</p>
            )}
          </div>

          {/* Content */}
          <div className="px-8 pb-8">
            {/* Error message */}
            {error && (
              <div className="mb-4 p-3 rounded-xl bg-red-500/10 border border-red-500/20 text-red-400 text-sm">
                {error}
              </div>
            )}

            {/* Success message */}
            {successMessage && view !== 'check-email' && (
              <div className="mb-4 p-3 rounded-xl bg-emerald-500/10 border border-emerald-500/20 text-emerald-400 text-sm">
                {successMessage}
              </div>
            )}

            {view === 'login' && renderLoginForm()}
            {view === 'register' && renderRegisterForm()}
            {view === 'forgot-password' && renderForgotPasswordForm()}
            {view === 'verify-email' && renderVerifyEmail()}
            {view === 'check-email' && renderCheckEmail()}
          </div>
        </div>
      </div>
    </Fragment>
  );
}


